import { useState } from 'react'
import { ChevronDown, ChevronRight, Zap } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { getActionTypeIcon, getActionTypeLabel } from '@/constants/actions'
import { formatActionResult, formatParameters, formatResultsDetailed } from '@/lib/actionFormatters'
import type { PartyAction } from '@/types'

interface ActionHistoryEntryProps {
  action: PartyAction
  targetName: string | null
}

const STATUS_STYLES: Record<string, { label: string; className: string }> = {
  queued: { label: 'Queued', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800' },
  resolved: { label: 'Resolved', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800' },
  cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800' },
}

function formatSubtype(subtype: string): string {
  return subtype
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export function ActionHistoryEntry({ action, targetName }: ActionHistoryEntryProps) {
  const [expanded, setExpanded] = useState(false)

  const icon = getActionTypeIcon(action.action_type)
  const label = getActionTypeLabel(action.action_type)
  const statusStyle = STATUS_STYLES[action.status] ?? STATUS_STYLES.queued
  const resultSummary =
    action.status === 'resolved'
      ? formatActionResult(action.action_type, action.results)
      : action.status === 'cancelled'
        ? 'Cancelled'
        : 'Awaiting resolution'

  const subtypeDisplay = action.action_subtype
    ? formatSubtype(action.action_subtype)
    : null

  return (
    <div className="border rounded-lg bg-card">
      {/* Compact Row */}
      <button
        className="w-full text-left px-3 py-2.5 flex items-center gap-2 hover:bg-muted/50 transition-colors rounded-lg"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Expand icon */}
        <span className="text-muted-foreground shrink-0">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>

        {/* Action icon */}
        <span className="text-lg shrink-0">{icon}</span>

        {/* Main info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm">{label}</span>
            {subtypeDisplay && (
              <span className="text-xs text-muted-foreground">
                {subtypeDisplay}
              </span>
            )}
            {targetName && (
              <span className="text-xs text-muted-foreground truncate">
                — {targetName}
              </span>
            )}
          </div>
          {action.status === 'resolved' && (
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              {resultSummary}
            </p>
          )}
        </div>

        {/* Right side metadata */}
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-muted-foreground flex items-center gap-0.5">
            <Zap className="h-3 w-3" />
            {action.action_points_spent}
          </span>
          <span className="text-xs text-muted-foreground font-mono">
            T{action.tick}
          </span>
          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${statusStyle.className}`}>
            {statusStyle.label}
          </Badge>
        </div>
      </button>

      {/* Expanded Detail */}
      {expanded && (
        <div className="px-3 pb-3 border-t">
          <div className="pt-3 space-y-3">
            {/* Result Summary */}
            <div>
              <h4 className="text-xs font-medium text-muted-foreground mb-1">Result</h4>
              <p className="text-sm">{resultSummary}</p>
            </div>

            {/* Result Details (for resolved actions) */}
            {action.status === 'resolved' && action.results && (
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-1">Details</h4>
                <DetailTable entries={formatResultsDetailed(action.action_type, action.results)} />
              </div>
            )}

            {/* Parameters */}
            {action.parameters && Object.keys(action.parameters).length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-1">Parameters</h4>
                <DetailTable entries={formatParameters(action.parameters)} />
              </div>
            )}

            {/* Target Info */}
            <div className="flex gap-4 text-xs text-muted-foreground">
              <span>Target type: {action.target_type}</span>
              {targetName && <span>Target: {targetName}</span>}
              <span>Submitted: {new Date(action.created_at).toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function DetailTable({ entries }: { entries: Array<{ label: string; value: string }> }) {
  if (entries.length === 0) {
    return <p className="text-xs text-muted-foreground italic">No details available</p>
  }

  return (
    <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
      {entries.map((entry, i) => (
        <div key={i} className="contents">
          <span className="text-xs text-muted-foreground">{entry.label}</span>
          <span className="text-xs truncate">{entry.value}</span>
        </div>
      ))}
    </div>
  )
}
